import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * ListRow: generic tappable row (roster links, settings-like lists, menus).
 * Props:
 * - title, subtitle, overline (small caps label above the title)
 * - icon: Feather icon name on the left, or `left` (node) for custom content
 * - value: right-hand text; `right` (node) replaces value and chevron
 * - onPress; showChevron (default: true when pressable)
 * - tone: 'raised' (default, to-do style) | 'card' (quieter, e.g. finished items)
 * - disabled, accessibilityHint, style
 */
const ListRow = ({
	title,
	subtitle,
	overline,
	icon,
	left,
	value,
	right,
	onPress,
	showChevron,
	tone = 'raised',
	disabled = false,
	accessibilityHint,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isPressable = Boolean(onPress) && !disabled;
	const hasChevron = showChevron ?? isPressable;

	const content = (
		<>
			{left || (Boolean(icon) && <Feather name={icon} size={22} color={colors.accent} />)}
			<View style={styles.body}>
				{Boolean(overline) && <Text style={styles.overline}>{overline}</Text>}
				<Text style={[styles.title, disabled && styles.disabledText]}>{title}</Text>
				{Boolean(subtitle) && <Text style={styles.subtitle}>{subtitle}</Text>}
			</View>
			{right || (Boolean(value) && <Text style={styles.value}>{value}</Text>)}
			{!right && hasChevron && <Feather name="chevron-right" size={22} color={colors.iconMuted} />}
		</>
	);

	const rowStyle = [styles.row, tone === 'card' && styles.card, style];
	const accessibilityLabel = [overline, title, subtitle, value].filter(Boolean).join(', ');

	if (!isPressable) {
		return (
			<View style={rowStyle} accessible={true} accessibilityLabel={accessibilityLabel} accessibilityState={{ disabled }}>
				{content}
			</View>
		);
	}

	return (
		<Pressable
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityHint={accessibilityHint}
			style={({ pressed }) => [rowStyle, pressed && styles.pressed]}
		>
			{content}
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type, space } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: touch.row,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	card: {
		borderColor: colors.card,
		backgroundColor: colors.card,
	},
	pressed: {
		opacity: 0.85,
	},
	body: {
		flex: 1,
		gap: 2,
	},
	overline: {
		...type.overline,
		letterSpacing: 0.48,
		color: colors.textSecondary,
	},
	title: {
		...type.rowTitle,
		color: colors.text,
	},
	subtitle: {
		...type.secondary,
		color: colors.textSecondary,
	},
	value: {
		...type.secondaryStrong,
		color: colors.textSecondary,
	},
	disabledText: {
		color: colors.disabledText,
	},
});

export default ListRow;
